import React, { useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, UserPlusIcon, ClipboardListIcon, ListChecksIcon, FileSignatureIcon, StethoscopeIcon, HeartHandshakeIcon, BoxIcon } from 'lucide-react';
interface Module {
  num: string;
  title: string;
  sub: string;
  desc: string;
  bullets: string[];
  Icon: BoxIcon;
}
export function Modules() {
  const [activeIndex, setActiveIndex] = useState(0);
  const modules: Module[] = [{
    num: 'TOMO 01',
    title: 'Primer contacto y recepción',
    sub: 'La base que tu asistente necesita',
    desc: 'Todo lo que pasa desde que el paciente llama hasta que se sienta en el sillón — estandarizado y listo para delegar.',
    bullets: ['Ficha de registro del paciente nuevo', 'Cuestionario de motivo de consulta', 'Guion de bienvenida para el asistente', 'Checklist de apertura del consultorio'],
    Icon: UserPlusIcon
  }, {
    num: 'TOMO 02',
    title: 'Historia clínica y anamnesis',
    sub: 'Tu documentación clínica en orden',
    desc: 'La ficha clínica que te protege legalmente y que cualquier profesional puede completar sin depender de tu memoria.',
    bullets: ['Historia clínica odontológica completa', 'Odontograma FDI estandarizado', 'Ficha de alertas del paciente', 'Registro de evolución por sesión'],
    Icon: ClipboardListIcon
  }, {
    num: 'TOMO 03',
    title: 'Plan de tratamiento',
    sub: 'Presentá el diagnóstico sin improvisar',
    desc: 'Cómo organizar el tratamiento por fases, presentar el presupuesto de forma clara y manejar las objeciones del paciente.',
    bullets: ['Plantilla de plan de tratamiento por fases', 'Presupuesto presentable al paciente', 'Guion de presentación del diagnóstico', 'Manejo de las 5 objeciones frecuentes'],
    Icon: ListChecksIcon
  }, {
    num: 'TOMO 04',
    title: 'Consentimientos informados',
    sub: 'Respaldo legal en cada procedimiento',
    desc: 'Seis formularios basados en la Ley 26.529 de Derechos del Paciente — listos para firmar antes de cada procedimiento.',
    bullets: ['Consentimiento general', 'Cirugía oral, endodoncia, blanqueamiento', 'Ortodoncia e implantes', 'Instrucciones pre-tratamiento'],
    Icon: FileSignatureIcon
  }, {
    num: 'TOMO 05',
    title: 'Ejecución del tratamiento',
    sub: 'Protocolos para lo que pasa todos los días',
    desc: 'Los protocolos paso a paso de los procedimientos más frecuentes — y el plan de emergencias para lo que no querés que pase.',
    bullets: ['Protocolo de anestesia local', 'Protocolo de restauraciones directas', '6 emergencias médicas con pasos claros', 'Checklist de cierre del consultorio'],
    Icon: StethoscopeIcon
  }, {
    num: 'TOMO 06',
    title: 'Alta y seguimiento',
    sub: 'El paciente se va — y vuelve',
    desc: 'Las herramientas para cerrar cada tratamiento de forma impecable y lograr que el paciente vuelva sin tener que perseguirlo.',
    bullets: ['Instrucciones post-tratamiento por procedimiento', '5 plantillas de WhatsApp para seguimiento', 'Protocolo de reactivación de pacientes inactivos', 'Encuesta de satisfacción lista para usar'],
    Icon: HeartHandshakeIcon
  }];
  const active = modules[activeIndex] ?? modules[0];
  return <section className="bg-white px-5 py-10 md:py-[70px] text-center" data-id="element-119">
      <div className="mx-auto max-w-[1000px]" data-id="element-120">
        <p className="mb-3 font-montserrat text-[11px] font-extrabold uppercase tracking-[3px] text-brandRed" data-id="element-121">
          Contenido completo
        </p>
        <h2 className="mb-2 font-bebas text-[clamp(30px,5vw,52px)] text-navy" data-id="element-122">
          Los 6 Tomos del Kit
        </h2>
        <p className="mb-6 md:mb-[44px] text-[16px] text-brandGray" data-id="element-123">
          Tu hoja de ruta para sistematizar el consultorio de punta a punta
        </p>

        {/* Mobile Accordion — compact */}
        <div className="block md:hidden space-y-2.5 text-left" data-id="element-124">
          {modules.map((mod, i) => {
          const isOpen = activeIndex === i;
          return <div key={i} className="overflow-hidden rounded-xl border border-navy-mid bg-navy" data-id="element-125">
                <button onClick={() => setActiveIndex(isOpen ? -1 : i)} aria-expanded={isOpen} className="flex w-full items-center gap-3 p-4 text-left" data-id="element-126">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brandRed/15 text-brandRed" data-id="element-127">
                    <mod.Icon className="h-5 w-5" strokeWidth={2} aria-hidden="true" data-id="element-128" />
                  </span>
                  <div className="min-w-0 flex-1" data-id="element-129">
                    <p className="font-bebas text-[12px] tracking-[2px] text-brandRed" data-id="element-130">
                      {mod.num}
                    </p>
                    <p className="font-montserrat text-[14px] font-extrabold leading-tight text-white" data-id="element-131">
                      {mod.title}
                    </p>
                  </div>
                  <motion.div animate={{
                rotate: isOpen ? 180 : 0
              }} transition={{
                duration: 0.2
              }} data-id="element-132">
                    <ChevronDownIcon className="h-5 w-5 text-brandRed" data-id="element-133" />
                  </motion.div>
                </button>

                <AnimatePresence initial={false} data-id="element-134">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} data-id="element-135">
                      <div className="px-4 pb-4 pt-0" data-id="element-136">
                        <p className="mb-2.5 font-montserrat text-[11px] font-semibold uppercase tracking-[1px] text-gold" data-id="element-137">
                          {mod.sub}
                        </p>
                        <p className="mb-3 text-[13px] leading-relaxed text-white/65" data-id="element-138">
                          {mod.desc}
                        </p>
                        <ul className="list-none space-y-1.5" data-id="element-139">
                          {mod.bullets.map((bullet, j) => <li key={j} className="relative pl-4 text-[12px] text-white/55" data-id="element-140">
                              <span className="absolute left-0 font-bold text-brandRed" data-id="element-141">
                                →
                              </span>
                              {bullet}
                            </li>)}
                        </ul>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>

        {/* Desktop Interactive Tabs */}
        <div className="hidden md:grid md:grid-cols-[300px_1fr] gap-6 text-left" data-id="element-142">
          <div className="flex flex-col gap-2" data-id="element-143">
            {modules.map((mod, i) => {
            const isActive = activeIndex === i;
            return <button key={i} onClick={() => setActiveIndex(i)} className={`relative flex items-center gap-3 overflow-hidden rounded-lg p-3.5 text-left transition-all ${isActive ? 'bg-navy shadow-lg' : 'bg-offWhite hover:bg-light-gray/50'}`} data-id="element-144">
                  {isActive && <motion.div layoutId="activeTabIndicator" className="absolute left-0 top-0 bottom-0 w-1 bg-brandRed" data-id="element-145" />}
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors ${isActive ? 'bg-brandRed/20 text-brandRed' : 'bg-navy/5 text-navy'}`} data-id="element-146">
                    <mod.Icon className="h-[18px] w-[18px]" strokeWidth={2} aria-hidden="true" data-id="element-147" />
                  </span>
                  <div data-id="element-148">
                    <p className={`font-bebas text-[12px] tracking-[2px] ${isActive ? 'text-brandRed' : 'text-brandGray'}`} data-id="element-149">
                      {mod.num}
                    </p>
                    <p className={`font-montserrat text-[14px] font-extrabold leading-tight ${isActive ? 'text-white' : 'text-navy'}`} data-id="element-150">
                      {mod.title}
                    </p>
                  </div>
                </button>;
          })}
          </div>

          <div className="relative overflow-hidden rounded-xl bg-navy p-8 shadow-xl" data-id="element-151">
            <AnimatePresence mode="wait" data-id="element-152">
              <motion.div key={activeIndex} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} className="h-full" data-id="element-153">
                <span className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-xl bg-brandRed/15 text-brandRed" data-id="element-154">
                  <active.Icon className="h-7 w-7" strokeWidth={2} aria-hidden="true" data-id="element-155" />
                </span>
                <p className="mb-2 font-bebas text-[14px] tracking-[2px] text-brandRed" data-id="element-156">
                  {active.num}
                </p>
                <h3 className="mb-3 font-montserrat text-[24px] font-extrabold leading-tight text-white" data-id="element-157">
                  {active.title}
                </h3>
                <p className="mb-4 inline-block rounded bg-gold/10 px-3 py-1 font-montserrat text-[12px] font-bold uppercase tracking-[1px] text-gold" data-id="element-158">
                  {active.sub}
                </p>
                <p className="mb-6 text-[15px] leading-relaxed text-white/70" data-id="element-159">
                  {active.desc}
                </p>

                <div className="rounded-lg border border-white/10 bg-white/5 p-5" data-id="element-160">
                  <p className="mb-3 font-montserrat text-[12px] font-bold uppercase tracking-[1px] text-white/50" data-id="element-161">
                    Qué incluye este tomo:
                  </p>
                  <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-id="element-162">
                    {active.bullets.map((bullet, j) => <li key={j} className="flex items-start gap-2 text-[13px] text-white/80" data-id="element-163">
                        <span className="mt-0.5 font-bold text-brandRed" data-id="element-164">
                          →
                        </span>
                        <span data-id="element-165">{bullet}</span>
                      </li>)}
                  </ul>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>;
}